import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import apiClient from "@/lib/api-client";

export type PaymentStatus = "pending" | "completed" | "verified" | "failed";

/**
 * Creates a payment intent and sends the browser to Snippe's hosted checkout.
 * The API prices the purchase and creates the Snippe payment link; the
 * student comes back to `returnPath` with `?payment_id=` once they are done.
 */
export async function startSnippeCheckout(input: {
  type: "course" | "premium" | "verification" | "tip";
  returnPath: string;
  referenceId?: string | number;
}): Promise<void> {
  const res = await apiClient.post("/students/me/payments/intents", {
    currency: "TZS",
    type: input.type,
    payment_method: "snippe",
    reference_id: input.referenceId != null ? String(input.referenceId) : undefined,
    return_path: input.returnPath,
  });
  const checkoutUrl: string | undefined = res.data?.data?.checkout_url;
  if (!checkoutUrl) {
    throw new Error("The payment link could not be created. Please try again.");
  }
  window.location.assign(checkoutUrl);
}

// Snippe can send the payer back before the mobile-money confirmation has
// reached the session, so a pending answer is retried every couple of seconds
// for up to about a minute. The API also re-checks open checkouts in the
// background, so a payment still pending after this settles on its own.
const CONFIRM_ATTEMPTS = 30;
const CONFIRM_INTERVAL_MS = 2000;

/**
 * Handles the return from Snippe checkout on a page that started one. It asks
 * the API to check the payment with Snippe, tells the student the outcome and
 * strips `payment_id` from the address bar.
 */
export function usePaymentReturn(onSettled?: (status: PaymentStatus) => void) {
  const queryClient = useQueryClient();
  const handled = useRef(false);
  const onSettledRef = useRef(onSettled);
  onSettledRef.current = onSettled;

  useEffect(() => {
    if (handled.current || typeof window === "undefined") return;
    const url = new URL(window.location.href);
    const paymentId = url.searchParams.get("payment_id");
    if (!paymentId || !/^\d+$/.test(paymentId)) return;
    handled.current = true;

    url.searchParams.delete("payment_id");
    window.history.replaceState(window.history.state, "", url.pathname + url.search + url.hash);

    // No cleanup cancels this: under StrictMode the effect is torn down and
    // re-run straight away, and the re-run is skipped by `handled`.
    const toastId = toast.loading("Confirming your payment…");

    const confirm = async (attempt: number): Promise<void> => {
      let status: PaymentStatus = "pending";
      try {
        const res = await apiClient.post(`/students/me/payments/intents/${paymentId}/confirm`);
        status = res.data?.data?.status ?? "pending";
      } catch {
        status = "pending";
      }

      if (status === "pending" && attempt < CONFIRM_ATTEMPTS) {
        await new Promise((resolve) => setTimeout(resolve, CONFIRM_INTERVAL_MS));
        return confirm(attempt + 1);
      }

      if (status === "completed" || status === "verified") {
        toast.success("Payment received", { id: toastId });
      } else if (status === "failed") {
        toast.error("Payment was not completed", {
          id: toastId,
          description: "You have not been charged. You can try again at any time.",
        });
      } else {
        toast.info("Payment is still processing", {
          id: toastId,
          description: "We'll notify you as soon as it clears.",
        });
      }
      queryClient.invalidateQueries({ queryKey: ["student-payments"] });
      onSettledRef.current?.(status);
    };

    void confirm(1);
  }, [queryClient]);
}
